import { useCallback, useEffect, useMemo, useState } from "react";

import toast from "react-hot-toast";

import { Button, Dropdown, Icon, Modal, Popup, Table } from "semantic-ui-react";
import styled from "styled-components";

import type { OpenAPI } from "simplydo/interfaces";
import { Input } from "simplydo/react/component-library";

import api from "api";
import { useAppSelector } from "store";
import { countries } from "utils/countries";

const CompanySearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  .input-area {
    display: flex;
    flex-direction: column;
    > span {
      display: block;
      font-size: 12px;
      font-weight: 600;
    }
    &:not(:last-of-type) {
      margin-bottom: 15px;
    }
  }
`;

type CreditsafeManualMatchProps = {
  companyId: string;
  name: string;
  location?: OpenAPI.Schemas["Innointel2Companies"]["location"];
  isOpen?: boolean;
  onClose?: () => void;
  onError?: (errorMessage: string) => void;
};

type CreditsafeCompanyResult = {
  id: string;
  name: string;
  address?: { city?: string; postCode?: string; simpleValue?: string };
  country: string;
  createdAt: string;
  phoneNumbers?: string[];
  regNo: string;
  status: string;
  statusDescription: string;
  type: string;
};

const CreditsafeManualMatch = ({
  companyId,
  isOpen,
  onClose,
  name: propName,
  location: propLocation,
  onError,
}: CreditsafeManualMatchProps) => {
  const user = useAppSelector((state) => state.user);
  const [name, setName] = useState<string>("");
  const [location, setLocation] = useState<string>("");
  const [initialSearchDone, setInitialSearchDone] = useState<boolean>(false);
  const [loadingCompanies, setLoadingCompanies] = useState<boolean>(false);
  const [loadingReport, setLoadingReport] = useState<boolean>(false);
  const [countrySearchTerm, setCountrySearchTerm] = useState<string>("");
  const [companies, setCompanies] = useState<CreditsafeCompanyResult[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<string>("");

  // const dispatch = useSearchCompanyDispatch();

  const searchCompanies = useCallback(
    (newName = "", newLocation = "") => {
      setLoadingCompanies(true);
      setSelectedCompany("");
      if (newName) {
        setName(newName);
      }
      if (newLocation) {
        setLocation(newLocation);
      }
      api.verifications.searchForCompanies(
        newLocation || location,
        "",
        newName || name,
        "org",
        user.ownerOrganisation._id,
        ({ companies: newCompanies }) => {
          setCompanies(newCompanies);
          setLoadingCompanies(false);
        },
        (err) => {
          toast.error(err.message);
          setLoadingCompanies(false);
          if (onError) {
            onError(err.message);
          }
        },
        undefined,
      );
    },
    [location, name, onError, user.ownerOrganisation._id],
  );

  const assignCreditsafeInformation = useCallback(() => {
    setLoadingReport(true);
    api.innointel2.assignCreditsafeId(
      companyId,
      { creditsafeId: selectedCompany },
      () => {
        api.queryClient.invalidateQueries({
          queryKey: ["innointel2", "company", companyId],
        });
        onClose?.();
        setLoadingReport(false);
      },
      () => {
        onClose?.();
        setLoadingReport(false);
      },
    );
  }, [companyId, selectedCompany, onClose]);

  useEffect(() => {
    if (isOpen && !initialSearchDone) {
      setInitialSearchDone(true);
      let newLocation = "";
      setName(propName);
      if (propLocation?.search?.address?.iso2) {
        let countryIso2 = propLocation.search.address.iso2;
        if (countryIso2 === "UK") {
          countryIso2 = "GB";
        }
        newLocation = countryIso2;
        setLocation(countryIso2);
      } else if (propLocation?.search?.address?.country) {
        const countryNameLowerCase =
          propLocation.search.address.country.toLowerCase();
        const countryIso2 = countries.find(
          (country) =>
            country.name.toLowerCase().indexOf(countryNameLowerCase) > -1,
        )?.iso2;
        if (countryIso2) {
          newLocation = countryIso2;
          setLocation(countryIso2);
        }
      }
      if (propName && newLocation) {
        searchCompanies(propName, newLocation);
      }
    }
  }, [propName, propLocation, searchCompanies, initialSearchDone, isOpen]);

  const filteredCountries = useMemo(() => {
    const searchTerm = countrySearchTerm.toLowerCase();
    return countries.filter((country) =>
      country.name.toLowerCase().includes(searchTerm),
    );
  }, [countrySearchTerm]);

  const CountryPartialMatchIcon = useMemo(
    () => (
      <Popup
        on="hover"
        content="This piece of information matches the original company"
        trigger={
          <Icon name="check circle" color="green" style={{ marginLeft: 5 }} />
        }
      />
    ),
    [],
  );

  return (
    <Modal
      mountNode={document.getElementById("semantic-modal-mount-node")}
      open={isOpen}
      onClose={onClose}
    >
      <Modal.Header>
        <p>Find credit information for {propName}</p>
        <p style={{ fontWeight: 400, fontSize: 14 }}>
          We couldn't find credit information for this company automatically -
          Please search for an option below to link credit information.
        </p>
      </Modal.Header>
      <Modal.Content scrolling>
        <CompanySearchContainer>
          <div className="input-area">
            <span>Company name</span>
            <Input
              value={name}
              onChange={(_e, { value }) => {
                setName(value);
              }}
              fluid
            />
          </div>

          <div className="input-area">
            <span>Country</span>
            <Dropdown
              fluid
              selection
              value={location}
              onChange={(_e, { value }) => {
                setLocation(value as string);
                setCountrySearchTerm("");
              }}
              searchQuery={countrySearchTerm}
              search
              onSearchChange={(_e, { searchQuery }) =>
                setCountrySearchTerm(searchQuery)
              }
              options={filteredCountries.map((country) => ({
                key: country.iso2,
                value: country.iso2,
                text: country.name,
              }))}
            />
          </div>

          <div className="input-area">
            <Button
              fluid
              primary={!selectedCompany}
              icon={selectedCompany ? "check" : "search"}
              color={selectedCompany ? "green" : null}
              onClick={
                selectedCompany ?
                  () => assignCreditsafeInformation()
                : () => searchCompanies()
              }
              loading={loadingCompanies || loadingReport}
              disabled={loadingCompanies || loadingReport}
              content={
                selectedCompany ?
                  "Get credit information for this result"
                : "Search"
              }
            />
          </div>

          {companies.length > 0 ?
            <Table basic="very" selectable>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Company name</Table.HeaderCell>
                  <Table.HeaderCell>Address</Table.HeaderCell>
                  <Table.HeaderCell>Reg no</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                </Table.Row>
              </Table.Header>

              <Table.Body>
                {companies.map((company) => (
                  <Table.Row
                    key={company.id}
                    onClick={() =>
                      setSelectedCompany(
                        selectedCompany === company.id ? "" : company.id,
                      )
                    }
                    style={{ cursor: "pointer" }}
                    active={selectedCompany === company.id}
                  >
                    <Table.Cell>
                      {company.name}
                      {company.name.toLowerCase() === name.toLowerCase() ?
                        CountryPartialMatchIcon
                      : null}
                    </Table.Cell>
                    <Table.Cell>
                      {company?.address?.simpleValue}
                      {(
                        company?.address?.postCode?.toLowerCase() ===
                        propLocation?.address?.postcode?.toLowerCase()
                      ) ?
                        CountryPartialMatchIcon
                      : null}
                    </Table.Cell>
                    <Table.Cell>{company.regNo}</Table.Cell>
                    <Table.Cell>{company.statusDescription}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
          : null}
        </CompanySearchContainer>
      </Modal.Content>
    </Modal>
  );
};

export default CreditsafeManualMatch;
